import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useColorScheme } from '@mui/material/styles';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Checkbox, Tooltip, useMediaQuery } from '@mui/material';
import { AVAILABILITY, DAY_WIDTH, getMachineAvailability, isBookingOverdueReturn } from '../constants';
import { availabilityColors, availabilityColorsDark } from '../colors';
import { useBookingResize } from '../hooks/useBookingResize';
import type { Booking, BookingResizeEdge, Machine } from '../types';
import { BOOKING_PICKUP_LABELS, BULK_ACTION_LABELS, PLANNING_LABELS } from '../../../utils/labels';
import { appNow } from '../../../utils/timezone';

const LABEL_WIDTH_MOBILE = 100; // w-25
const LABEL_WIDTH_MOBILE_BULK = 144; // w-36
const LABEL_WIDTH_DESKTOP = 160; // w-40
const MD_BREAKPOINT = 768;
const ROW_HEIGHT = 64; // px, matches min-h-16

// Stripe gradient applied once to the rows wrapper — transparent cells show through
// to this single parent background, giving seamless diagonal lines across all rows.
const STRIPE = 'repeating-linear-gradient(135deg, var(--color-border-subtle) 0, var(--color-border-subtle) 0.25rem, var(--color-border) 0.25rem, var(--color-border) 0.5rem)';

// Open diagonal stripe for cells after an unconfirmed end date — same 135° angle as
// STRIPE but wider gaps (12px repeat, 2px line) for a lighter, more open appearance.
// Uses var(--color-surface) for the gap so the parent STRIPE doesn't bleed through.
const STRIPE_OPEN_LIGHT = 'repeating-linear-gradient(135deg, var(--color-surface) 0, var(--color-surface) 0.5rem, var(--color-border-subtle) 0.5rem, var(--color-border-subtle) 0.625rem)';
const STRIPE_OPEN_DARK  = 'repeating-linear-gradient(135deg, var(--color-surface) 0, var(--color-surface) 0.5rem, var(--color-border) 0.5rem, var(--color-border) 0.625rem)';

// Booking bar extension for an overdue hand-in (pickup confirmed, not yet handed in, past the
// booking's own end_date/end_time): same 135° diagonal-hatch idea as STRIPE_OPEN, but in the
// booking's own site colour (dashed instead of solid) so it reads as a continuation of the same
// booking rather than a differently-coloured, unrelated element. The gaps between stripes are a
// faint tint (not fully transparent) so the segment always reads as "a block", while the stripes
// themselves are strongly saturated so the warning stays prominent in the foreground.
function bookingOverdueStripe(color: string): string {
  return `repeating-linear-gradient(135deg, ${color}1a 0, ${color}1a 4px, ${color}b3 4px, ${color}b3 8px)`;
}

// Same 135° diagonal-hatch language as bookingOverdueStripe ("not yet a settled fact"), but
// noticeably lighter — this marks an in-progress booking-resize edit, not a warning, so it
// must read as "pending" rather than "urgent". Used for both the extending segment (space
// beyond the saved booking that would be added) and the shrinking segment (the saved area
// that would be cut off), so the confirmed part of the bar always stays visually distinct
// from whatever the current drag would change.
function bookingPendingStripe(color: string): string {
  return `repeating-linear-gradient(135deg, ${color}00 0, ${color}00 4px, ${color}59 4px, ${color}59 8px)`;
}

interface BarProps {
  left: number;
  width: number;
  clipsLeft: boolean;
  clipsRight: boolean;
}

function timeToFraction(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h * 60 + m) / 1440;
}

function getBarProps(
  startDate: string,
  endDate: string,
  startTime: string | undefined,
  endTime: string | undefined,
  viewStartStr: string,
  viewEndStr: string,
  days: number
): BarProps | null {
  if (startDate > viewEndStr || endDate < viewStartStr) return null;
  const viewStart = dayjs(viewStartStr);
  const clipsLeft = startDate < viewStartStr;
  const clipsRight = endDate > viewEndStr;
  const startDayIdx = clipsLeft ? 0 : dayjs(startDate).diff(viewStart, 'day');
  const endDayIdx = clipsRight ? days - 1 : Math.min(days - 1, dayjs(endDate).diff(viewStart, 'day'));
  // When clipping, ignore the time fraction so the bar is flush with the viewport edge.
  const startFraction = !clipsLeft && startTime ? timeToFraction(startTime) : 0;
  const endFraction = !clipsRight && endTime ? timeToFraction(endTime) : 1;
  // Use px instead of % so bars align pixel-perfectly with the fixed-width day cells.
  const left = (startDayIdx + startFraction) * DAY_WIDTH;
  const right = (endDayIdx + endFraction) * DAY_WIDTH;
  return { left, width: Math.max(DAY_WIDTH * 0.5, right - left), clipsLeft, clipsRight };
}

interface DayLabel {
  dateStr: string;
  num: number;
  weekday: string;
  isWeekend: boolean;
  isToday: boolean;
  monthLabel: string | null;
}

interface SortableMachineRowProps {
  machine: Machine;
  isReorderMode: boolean;
  isBulkMode: boolean;
  isSelected: boolean;
  isBulkSelected: boolean;
  isReadOnly: boolean;
  isDark: boolean;
  currentAvailColors: Record<string, string>;
  dayLabels: DayLabel[];
  viewStart: string;
  viewEndStr: string;
  days: number;
  hoveredCell: { machineId: string; day: number } | null;
  pickupHandinEnabled: boolean;
  onSelectMachine: (m: Machine) => void;
  onToggleBulkSelect: (id: string) => void;
  onDateClick: (machine: Machine, date: string) => void;
  onBookingClick: (bk: Booking) => void;
  onHoverCell: (cell: { machineId: string; day: number } | null) => void;
  onResizeBooking: (booking: Booking, edge: BookingResizeEdge, newDate: string) => void;
  onResizeBlocked: () => void;
}

function SortableMachineRow({
  machine,
  isReorderMode,
  isBulkMode,
  isSelected,
  isBulkSelected,
  isReadOnly,
  isDark,
  currentAvailColors,
  dayLabels,
  viewStart,
  viewEndStr,
  days,
  hoveredCell,
  pickupHandinEnabled,
  onSelectMachine,
  onToggleBulkSelect,
  onDateClick,
  onBookingClick,
  onHoverCell,
  onResizeBooking,
  onResizeBlocked,
}: SortableMachineRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: machine.id,
    disabled: !isReorderMode,
  });
  const resize = useBookingResize({
    machine,
    isReadOnly,
    onResize: onResizeBooking,
    onBlocked: onResizeBlocked,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const availKey = getMachineAvailability(machine, pickupHandinEnabled);
  const avail = AVAILABILITY[availKey];
  const dotColor = currentAvailColors[availKey] ?? currentAvailColors.available;
  const stripeOpen = isDark ? STRIPE_OPEN_DARK : STRIPE_OPEN_LIGHT;
  const todayStr = appNow().format('YYYY-MM-DD');
  const nowTimeStr = appNow().format('HH:mm');

  // Remainder area: the partial column at the right edge when the container width is not
  // an exact multiple of DAY_WIDTH. Show the same state as what the machine would be at
  // the view end: in-range → bg-surface, confirmed end → transparent (parent stripe),
  // unconfirmed end → chevron.
  const remainderEndedByViewEnd = !!(machine.end_date && machine.end_date <= viewEndStr);
  const remainderNotStartedByViewEnd = !!(machine.start_date && machine.start_date > viewEndStr);
  const remainderInRange = !remainderEndedByViewEnd && !remainderNotStartedByViewEnd;
  const hasUnconfirmedEnd = !!(machine.end_date && !machine.pickup_on_end_date && machine.end_date <= viewEndStr);
  // Pixel offset where the STRIPE_OPEN overlay begins: just after the end_date boundary (with
  // end_time fraction when set). One overlay div covers the whole right-out-of-range area so the
  // pattern is a single continuous background — same technique as STRIPE on the grid parent.
  const endDayIdx = hasUnconfirmedEnd ? dayjs(machine.end_date!).diff(dayjs(viewStart), 'day') : 0;
  const endTimeFracForOverlay = machine.end_time ? timeToFraction(machine.end_time) : 1;
  const stripeOpenLeft = hasUnconfirmedEnd
    ? (endTimeFracForOverlay < 1
        ? endDayIdx * DAY_WIDTH + endTimeFracForOverlay * DAY_WIDTH
        : (endDayIdx + 1) * DAY_WIDTH)
    : 0;

  const handleRowClick = () => {
    if (isReorderMode) return;
    if (isBulkMode) { onToggleBulkSelect(machine.id); return; }
    onSelectMachine(machine);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex h-16 border-b border-border-strong cursor-pointer"
      onClick={handleRowClick}
    >
      {/* Machine label column */}
      <div
        className={
          `px-2 py-3 md:p-4 sticky left-0 shrink-0 border-r border-border z-2 ${isBulkMode ? 'w-36' : 'w-25'} md:w-40 bg-surface flex items-center gap-1.5 md:gap-2 ` +
          (isBulkSelected
            ? 'bg-primary/5'
            : isSelected && !isReorderMode && !isBulkMode
            ? 'inset-shadow-[0.1875rem_0_0_0_var(--color-primary)]'
            : '')
        }
      >
        {isReorderMode && (
          <Tooltip title={PLANNING_LABELS.reorderDragHandle}>
            <span
              {...attributes}
              {...listeners}
              className="text-text-disabled cursor-grab active:cursor-grabbing shrink-0"
              aria-label={PLANNING_LABELS.reorderDragHandle}
              data-testid="drag-handle"
            >
              <DragIndicatorIcon fontSize="small" />
            </span>
          </Tooltip>
        )}
        {isBulkMode && (
          <Checkbox
            size="small"
            checked={isBulkSelected}
            onChange={(e) => { e.stopPropagation(); onToggleBulkSelect(machine.id); }}
            onClick={(e) => e.stopPropagation()}
            slotProps={{ input: { 'aria-label': BULK_ACTION_LABELS.checkboxLabel(machine.name) } }}
            className="shrink-0 -ml-1"
          />
        )}
        <div className="min-w-0">
          <p className="text-xs md:text-sm font-bold truncate mb-0 mt-0">{machine.name}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ backgroundColor: dotColor }}
            />
            <span className="text-xs truncate">{avail.label}</span>
          </div>
        </div>
      </div>

      {/* Timeline cells */}
      <div className="flex-1 relative">
        {/* Open stripe for the unconfirmed-end area — one continuous div spanning from the
            end-date boundary to the row's right edge, so the pattern is seamless within itself
            with no per-cell origin resets. Cells before this point stay fully transparent and
            show the single grandparent STRIPE (line ~700) through, exactly like the always-
            correct "confirmed end date" case. */}
        {hasUnconfirmedEnd && (
          <div
            className="absolute inset-y-0 right-0 pointer-events-none"
            style={{ left: stripeOpenLeft, backgroundImage: stripeOpen }}
          />
        )}
        <div className="absolute inset-0 flex">
          {dayLabels.map(({ dateStr, isWeekend }, i) => {
            const leftOutOfRange = !!(machine.start_date && dateStr < machine.start_date);
            // Cells after end_date: use chevron when end date is not yet confirmed, stripe otherwise
            const rightOutOfRange = !!(machine.end_date && dateStr > machine.end_date);
            const outOfRange = leftOutOfRange || rightOutOfRange;
            // Partial intra-day boundary: left stripe for start day, right stripe/open for end day
            const isStartDay = !outOfRange && machine.start_date === dateStr && !!machine.start_time;
            const isEndDay = !outOfRange && machine.end_date === dateStr && !!machine.end_time;
            // Last in-range cell (end boundary): gets custom vertical line instead of normal border-r
            const isEndBoundary = !outOfRange && machine.end_date === dateStr;
            const startTimeFrac = isStartDay ? timeToFraction(machine.start_time!) : 0;
            const endTimeFrac = isEndDay ? timeToFraction(machine.end_time!) : 1;
            // showCover: bg-surface div covers the in-range portion of boundary cells
            const showCover = (isStartDay && startTimeFrac > 0) || (isEndDay && endTimeFrac < 1);
            const hasBooking = machine.bookings?.some(
              (bk) => bk.start_date <= dateStr && bk.end_date >= dateStr
            );
            const isHovered =
              !isReadOnly &&
              !isReorderMode &&
              !hasBooking &&
              !outOfRange &&
              !machine.inactive &&
              hoveredCell?.machineId === machine.id &&
              hoveredCell?.day === i;
            // Hide border-r on out-of-range and end-boundary cells — a custom vertical
            // line overlay is rendered instead for the boundary, and stripe/open-stripe
            // backgrounds handle the out-of-range area.
            const hideBorder = outOfRange || isEndBoundary;
            // Full in-range cells get bg-surface to cover the parent stripe
            const inRangeSolid = !outOfRange && !showCover;
            return (
              <div
                key={i}
                className={`w-9 shrink-0 h-full relative flex items-center justify-center${hideBorder ? '' : ' border-r border-border-subtle'}${!isHovered ? ' cursor-default' : ''}${inRangeSolid ? ' bg-surface' : ''}`}
                onClick={(e) => {
                  if (isReorderMode) return;
                  e.stopPropagation();
                  if (!hasBooking && !isReadOnly && !outOfRange && !machine.inactive)
                    onDateClick(machine, dateStr);
                  else
                    onSelectMachine(machine);
                }}
                onMouseEnter={() => !isReorderMode && onHoverCell({ machineId: machine.id, day: i })}
                onMouseLeave={() => !isReorderMode && onHoverCell(null)}
                title={dateStr}
              >
                {/* Solid surface covers only the in-range portion of boundary cells */}
                {showCover && (
                  <div
                    className="absolute inset-y-0 bg-surface pointer-events-none"
                    style={{
                      left: isStartDay && startTimeFrac > 0 ? `${startTimeFrac * 100}%` : '0%',
                      right: isEndDay && endTimeFrac < 1 ? `${(1 - endTimeFrac) * 100}%` : '0%',
                    }}
                  />
                )}
                {/* Vertical end boundary line — dashed for unconfirmed, solid thick for confirmed */}
                {isEndBoundary && (
                  <div
                    className="absolute inset-y-0 pointer-events-none"
                    style={
                      isEndDay
                        ? {
                            left: `calc(${endTimeFrac * 100}% - 1px)`,
                            width: machine.pickup_on_end_date ? '2px' : '1px',
                            background: machine.pickup_on_end_date
                              ? 'var(--color-foreground)'
                              : `repeating-linear-gradient(to bottom, var(--color-foreground) 0, var(--color-foreground) 4px, transparent 4px, transparent 8px)`,
                          }
                        : {
                            right: '-1px',
                            width: machine.pickup_on_end_date ? '2px' : '1px',
                            background: machine.pickup_on_end_date
                              ? 'var(--color-foreground)'
                              : `repeating-linear-gradient(to bottom, var(--color-foreground) 0, var(--color-foreground) 4px, transparent 4px, transparent 8px)`,
                          }
                    }
                  />
                )}
                {/* Tint overlays for in-range non-boundary cells */}
                {inRangeSolid && isWeekend && (
                  <div className="absolute inset-0 pointer-events-none bg-black/[0.035]" />
                )}
                {isHovered && (
                  <div className="w-3.5 h-3.5 rounded-full bg-primary flex items-center justify-center pointer-events-none opacity-[0.85]">
                    <span className="text-xs text-white">+</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Remainder: the partial column at the right edge of the timeline when the
            container width is not an exact multiple of DAY_WIDTH. Match the machine state. */}
        <div
          className={`absolute inset-y-0 right-0${remainderInRange ? ' bg-surface' : ''}`}
          style={{ left: days * DAY_WIDTH }}
        />

        {/* Dashed boundary line for unconfirmed whole-day end — rendered after booking bars
            so it sits on top of everything. The per-cell isEndBoundary line (right:-1px) is
            behind the STRIPE_OPEN timeline background, so we draw it explicitly here. */}
        {hasUnconfirmedEnd && !machine.end_time && endDayIdx >= 0 && (
          <div
            className="absolute top-0 w-px pointer-events-none"
            style={{
              height: ROW_HEIGHT,
              left: stripeOpenLeft,
              background: 'repeating-linear-gradient(to bottom, var(--color-foreground) 0, var(--color-foreground) 4px, transparent 4px, transparent 8px)',
            }}
          />
        )}

        {/* Booking bars */}
        {!isReorderMode && machine.bookings?.map((bk) => {
          const bar = getBarProps(
            bk.start_date,
            bk.end_date,
            bk.start_time,
            bk.end_time,
            viewStart,
            viewEndStr,
            days
          );
          if (!bar) return null;
          const color = bk.sites?.color ?? '#a8a29e';
          // Overdue hand-in: pickup confirmed, not yet handed in, past the booking's own end —
          // extend the bar visually up to "now" with a dashed/hatched segment so it reads as
          // still occupied instead of silently ending, mirroring the machine-level unconfirmed-
          // end treatment above.
          const overdue = isBookingOverdueReturn(bk, pickupHandinEnabled);
          const overrunBar = overdue
            ? getBarProps(bk.end_date, todayStr, bk.end_time, nowTimeStr, viewStart, viewEndStr, days)
            : null;
          // Never show a resize handle for an edge that's clipped off-screen — it wouldn't
          // represent the booking's real boundary.
          const activeDrag = resize.drag?.bookingId === bk.id ? resize.drag : null;
          const dragDeltaPx = activeDrag ? activeDrag.deltaDays * DAY_WIDTH : 0;
          const showStartHandle = resize.canResize() && !bar.clipsLeft;
          const showEndHandle = resize.canResize() && !bar.clipsRight;
          // The saved bar (below) never moves during a drag — only the handle position and the
          // pending segment track the in-progress delta, so the already-confirmed period always
          // stays visually distinct from what the current drag would add or cut off. Nothing is
          // written until the pointer is released (useBookingResize's endDrag).
          const displayLeft = bar.left + (activeDrag?.edge === 'start' ? dragDeltaPx : 0);
          const displayWidth =
            bar.width + (activeDrag ? (activeDrag.edge === 'start' ? -dragDeltaPx : dragDeltaPx) : 0);
          const isExtending = !!activeDrag && dragDeltaPx !== 0 && (
            (activeDrag.edge === 'end' && dragDeltaPx > 0) || (activeDrag.edge === 'start' && dragDeltaPx < 0)
          );
          const isShrinking = !!activeDrag && dragDeltaPx !== 0 && !isExtending;
          let pendingLeft = 0;
          let pendingWidth = 0;
          if (activeDrag && dragDeltaPx !== 0) {
            if (activeDrag.edge === 'end') {
              pendingLeft = isExtending ? bar.left + bar.width : bar.left + bar.width + dragDeltaPx;
              pendingWidth = Math.abs(dragDeltaPx);
            } else {
              pendingLeft = isExtending ? bar.left + dragDeltaPx : bar.left;
              pendingWidth = Math.abs(dragDeltaPx);
            }
          }
          return (
            // This wrapper is a plain block element (no CSS of its own) — every child below is
            // position:absolute, so none contribute to its box and it always renders at zero
            // size. Never put a data-testid here expecting it to be "visible": a real browser
            // (unlike jsdom) reports a zero-size element as hidden even though its absolutely
            // positioned children render elsewhere on screen just fine.
            <div key={bk.id}>
              <div
                data-testid={`booking-bar-${bk.id}`}
                className={`absolute top-3 h-10 flex flex-col justify-center px-2 overflow-hidden cursor-pointer hover:brightness-95 transition-all border-t border-b${bar.clipsLeft ? '' : ' border-l rounded-l'}${!bar.clipsRight && !overrunBar ? ' border-r rounded-r' : ''}`}
                style={{
                  left: `${bar.left}px`,
                  ...(bar.clipsRight ? { right: overrunBar ? undefined : 0 } : { width: `${bar.width}px` }),
                  backgroundColor: isDark ? `${color}80` : `${color}4d`,
                  borderColor: color,
                  color: isDark ? '#fafaf9' : '#1c1917',
                }}
                title={`${bk.subcontractor?.name ?? ''} — ${[bk.subcontractor_user?.first_name, bk.subcontractor_user?.last_name].filter(Boolean).join(' ')} – ${bk.sites?.name ?? ''}${overdue ? ` — ${BOOKING_PICKUP_LABELS.overdueBadge}` : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onBookingClick(bk);
                }}
              >
                <span className="truncate text-xs font-semibold leading-tight">
                  {bk.subcontractor?.name}
                </span>
                <span className="truncate text-xs leading-tight">
                  {[bk.subcontractor_user?.first_name, bk.subcontractor_user?.last_name]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </div>
              {(isExtending || isShrinking) && (
                <div
                  data-testid={isExtending ? `booking-resize-pending-add-${bk.id}` : `booking-resize-pending-remove-${bk.id}`}
                  className="absolute top-3 h-10 border-t border-b border-dashed pointer-events-none"
                  style={{
                    left: `${pendingLeft}px`,
                    width: `${pendingWidth}px`,
                    borderColor: color,
                    backgroundImage: bookingPendingStripe(color),
                  }}
                  title={isExtending ? PLANNING_LABELS.resizePendingAdd : PLANNING_LABELS.resizePendingRemove}
                />
              )}
              <div className="absolute top-3 h-10 pointer-events-none" style={{ left: `${displayLeft}px`, width: `${displayWidth}px` }}>
                {showStartHandle && (
                  <span
                    role="button"
                    tabIndex={-1}
                    data-testid={`resize-handle-start-${bk.id}`}
                    aria-label={PLANNING_LABELS.resizeDragHandleStart}
                    className="absolute inset-y-0 left-0 w-2.5 flex items-center justify-center cursor-col-resize touch-none pointer-events-auto"
                    onPointerDown={(e) => resize.startDrag(e, bk, 'start')}
                    onPointerMove={resize.moveDrag}
                    onPointerUp={resize.endDrag}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  >
                    <span className="w-0.5 h-4 rounded-full bg-current opacity-60" />
                  </span>
                )}
                {showEndHandle && (
                  <span
                    role="button"
                    tabIndex={-1}
                    data-testid={`resize-handle-end-${bk.id}`}
                    aria-label={PLANNING_LABELS.resizeDragHandleEnd}
                    className="absolute inset-y-0 right-0 w-2.5 flex items-center justify-center cursor-col-resize touch-none pointer-events-auto"
                    onPointerDown={(e) => resize.startDrag(e, bk, 'end')}
                    onPointerMove={resize.moveDrag}
                    onPointerUp={resize.endDrag}
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  >
                    <span className="w-0.5 h-4 rounded-full bg-current opacity-60" />
                  </span>
                )}
              </div>
              {overrunBar && (
                <div
                  data-testid="booking-overdue-bar"
                  className={`absolute top-3 h-10 border-t border-b border-dashed cursor-pointer hover:brightness-95 transition-all${overrunBar.clipsRight ? '' : ' border-r rounded-r'}`}
                  style={{
                    left: `${overrunBar.left}px`,
                    ...(overrunBar.clipsRight ? { right: 0 } : { width: `${overrunBar.width}px` }),
                    borderColor: color,
                    backgroundColor: isDark ? `${color}40` : `${color}26`,
                    backgroundImage: bookingOverdueStripe(color),
                  }}
                  title={BOOKING_PICKUP_LABELS.overdueBadge}
                  onClick={(e) => {
                    e.stopPropagation();
                    onBookingClick(bk);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PlanningGridProps {
  machines: Machine[];
  viewStart: string;
  selectedId?: string;
  isReadOnly: boolean;
  isReorderMode: boolean;
  isBulkMode: boolean;
  selectedBulkIds: Set<string>;
  pickupHandinEnabled: boolean;
  onSelectMachine: (m: Machine) => void;
  onToggleBulkSelect: (id: string) => void;
  onDateClick: (machine: Machine, date: string) => void;
  onBookingClick: (bk: Booking) => void;
  onShiftDays: (n: number) => void;
  onReorder: (orderedIds: string[]) => void;
  onResizeBooking: (booking: Booking, edge: BookingResizeEdge, newDate: string) => void;
  onResizeBlocked: () => void;
}

export default function PlanningGrid({
  machines,
  viewStart,
  onSelectMachine,
  onToggleBulkSelect,
  onDateClick,
  onBookingClick,
  onShiftDays,
  onReorder,
  onResizeBooking,
  onResizeBlocked,
  selectedId,
  isReadOnly,
  isReorderMode,
  isBulkMode,
  selectedBulkIds,
  pickupHandinEnabled,
}: PlanningGridProps) {
  const { mode } = useColorScheme();
  const isDark = mode === 'dark';
  const currentAvailColors = isDark ? availabilityColorsDark : availabilityColors;

  const [hoveredCell, setHoveredCell] = useState<{ machineId: string; day: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Desktop pan-to-shift: hold+drag left/right to navigate days
  const panPointerIdRef = useRef<number | null>(null);
  const panStartXRef = useRef<number | null>(null);
  const panStartYRef = useRef<number | null>(null);
  const panActiveRef = useRef(false);
  const panAccumRef = useRef(0);
  const panTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panDidDragRef = useRef(false);
  const [panCursor, setPanCursor] = useState<'default' | 'grab' | 'grabbing'>('default');
  const wheelAccumRef = useRef(0);

  const sensors = useSensors(useSensor(PointerSensor));
  const isNarrowViewport = useMediaQuery(`(max-width:${MD_BREAKPOINT}px)`);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => setContainerWidth(entries[0].contentRect.width));
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => {
    if (panTimerRef.current) clearTimeout(panTimerRef.current);
    if (panHintTimerRef.current) clearTimeout(panHintTimerRef.current);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
      e.preventDefault();
      if (wheelAccumRef.current !== 0 && Math.sign(e.deltaX) !== Math.sign(wheelAccumRef.current)) {
        wheelAccumRef.current = 0;
      }
      wheelAccumRef.current += e.deltaX;
      if (Math.abs(wheelAccumRef.current) >= DAY_WIDTH) {
        const n = Math.floor(Math.abs(wheelAccumRef.current) / DAY_WIDTH);
        onShiftDays(wheelAccumRef.current > 0 ? n : -n);
        wheelAccumRef.current -= Math.sign(wheelAccumRef.current) * n * DAY_WIDTH;
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [onShiftDays]);

  const activatePan = (element: HTMLElement) => {
    panActiveRef.current = true;
    setPanCursor('grabbing');
    if (panPointerIdRef.current !== null) {
      try { element.setPointerCapture(panPointerIdRef.current); } catch {}
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (isReorderMode || e.pointerType === 'touch' || e.button !== 0) return;
    panStartXRef.current = e.clientX;
    panStartYRef.current = e.clientY;
    panPointerIdRef.current = e.pointerId;
    panAccumRef.current = 0;
    panDidDragRef.current = false;
    panActiveRef.current = false;
    const el = e.currentTarget as HTMLElement;
    panHintTimerRef.current = setTimeout(() => setPanCursor('grab'), 200);
    panTimerRef.current = setTimeout(() => activatePan(el), 700);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isReorderMode || e.pointerType === 'touch') return;
    if (!panActiveRef.current) {
      // Cancel hold if user drifts more than 10px from press position
      if (panStartXRef.current !== null && (
        Math.abs(e.clientX - panStartXRef.current) > 10 ||
        Math.abs(e.clientY - (panStartYRef.current ?? e.clientY)) > 10
      )) {
        resetPan();
      }
      return;
    }
    panAccumRef.current += e.movementX;
    if (Math.abs(panAccumRef.current) >= DAY_WIDTH) {
      const n = Math.floor(Math.abs(panAccumRef.current) / DAY_WIDTH);
      onShiftDays(panAccumRef.current < 0 ? n : -n);
      panAccumRef.current -= Math.sign(panAccumRef.current) * n * DAY_WIDTH;
      panDidDragRef.current = true;
    }
  };

  const resetPan = () => {
    if (panTimerRef.current) { clearTimeout(panTimerRef.current); panTimerRef.current = null; }
    if (panHintTimerRef.current) { clearTimeout(panHintTimerRef.current); panHintTimerRef.current = null; }
    panStartXRef.current = null;
    panStartYRef.current = null;
    panPointerIdRef.current = null;
    panActiveRef.current = false;
    panAccumRef.current = 0;
    setPanCursor('default');
  };

  // Virtual scrolling — only active in normal mode (reorder mode needs all rows in DOM for DnD)
  const rowVirtualizer = useVirtualizer({
    count: machines.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isReorderMode) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isReorderMode || !touchStartRef.current || !onShiftDays) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    const days = Math.max(1, Math.round(Math.abs(dx) / DAY_WIDTH));
    onShiftDays(dx < 0 ? days : -days);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = machines.map((m) => m.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  // Based on the actual viewport, not containerWidth — the grid's own container shrinks
  // whenever the side panel opens, which must never flip the label column between its
  // mobile/desktop widths (that would shift every column, including the today marker,
  // purely because a sibling panel opened, not because the device is actually narrow).
  const labelWidth = isNarrowViewport
    ? (isBulkMode ? LABEL_WIDTH_MOBILE_BULK : LABEL_WIDTH_MOBILE)
    : LABEL_WIDTH_DESKTOP;
  const days =
    containerWidth > 0 ? Math.max(7, Math.floor((containerWidth - labelWidth) / DAY_WIDTH)) : 28;

  const today = appNow().format('YYYY-MM-DD');
  const startDayjs = dayjs(viewStart);
  const viewEndStr = startDayjs.add(days - 1, 'day').format('YYYY-MM-DD');

  const dayLabels = useMemo<DayLabel[]>(
    () =>
      Array.from({ length: days }, (_, i) => {
        const d = startDayjs.add(i, 'day');
        const dow = d.day();
        const dateStr = d.format('YYYY-MM-DD');
        return {
          dateStr,
          num: d.date(),
          weekday: d.toDate().toLocaleDateString('nl-BE', { weekday: 'short' })[0],
          isWeekend: dow === 0 || dow === 6,
          isToday: dateStr === today,
          monthLabel: i === 0 || d.date() === 1 ? PLANNING_LABELS.monthsShort[d.month()] : null,
        };
      }),
    [viewStart, days]
  );

  const todayIndex = dayLabels.findIndex(d => d.isToday);

  const getNowFraction = () => {
    const n = appNow();
    return (n.hour() * 60 + n.minute()) / 1440;
  };
  const [nowFraction, setNowFraction] = useState(getNowFraction);
  useEffect(() => {
    if (todayIndex === -1) return;
    const id = setInterval(() => setNowFraction(getNowFraction()), 60_000);
    return () => clearInterval(id);
  }, [todayIndex]);

  const sharedRowProps = {
    isReorderMode,
    isBulkMode,
    isReadOnly,
    isDark,
    currentAvailColors,
    dayLabels,
    viewStart,
    viewEndStr,
    days,
    hoveredCell,
    pickupHandinEnabled,
    onSelectMachine,
    onToggleBulkSelect,
    onDateClick,
    onBookingClick,
    onHoverCell: setHoveredCell,
    onResizeBooking,
    onResizeBlocked,
  };

  // Virtual items are empty in jsdom (no layout = 0 viewport height).
  // Fall back to rendering all rows so tests pass without mocking the virtualizer.
  const virtualItems = rowVirtualizer.getVirtualItems();
  const useVirtualList = !isReorderMode && virtualItems.length > 0;

  return (
    <div
      ref={containerRef}
      className={`flex-1 overflow-x-hidden overflow-y-auto overscroll-x-none${panCursor === 'grab' ? ' cursor-grab select-none' : panCursor === 'grabbing' ? ' cursor-grabbing select-none' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={resetPan}
      onPointerLeave={resetPan}
      onPointerCancel={resetPan}
      onClickCapture={(e) => {
        if (panDidDragRef.current) {
          e.stopPropagation();
          panDidDragRef.current = false;
        }
      }}
      onScroll={() => setHoveredCell(null)}
    >
      <div className="w-full relative">
        {/* Day header */}
        <div className="flex sticky top-0 z-10 bg-surface border-b border-border shrink-0">
          <div className={`border-r border-border px-3 ${isBulkMode ? 'w-36' : 'w-25'} md:w-40`} />
          <div className="flex">
            {dayLabels.map(({ num, weekday, isWeekend, isToday, monthLabel }, i) => (
              <div
                key={i}
                className={`pt-1 pb-1 w-9 shrink-0 text-center border-r border-border-subtle ${isToday ? 'bg-primary' : isWeekend ? 'bg-[var(--weekend-col-bg)]' : 'bg-surface'}`}
              >
                {monthLabel ? (
                  <div className={`font-bold text-xs ${isToday ? 'text-on-primary' : 'text-primary'}`}>
                    {monthLabel}
                  </div>
                ) : (
                  <div className="h-3.5" />
                )}
                <div className={`font-medium text-xs ${isToday ? 'text-on-primary/80' : 'text-text-disabled'}`}>
                  {weekday}
                </div>
                <div className={`font-bold text-xs leading-normal ${isToday ? 'text-on-primary' : isWeekend ? 'text-text-secondary' : 'text-foreground'}`}>
                  {num}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reorder mode banner */}
        {isReorderMode && (
          <div className="px-4 py-2 bg-primary/10 border-b border-primary/20 text-sm text-primary font-medium">
            {PLANNING_LABELS.reorderBanner}
          </div>
        )}

        {machines.length === 0 && (
          <div className="flex items-center justify-center h-48">
            <p className="text-sm text-text-secondary">{PLANNING_LABELS.noMachines}</p>
          </div>
        )}

        {/* Single background div: the stripe pattern is on this parent so transparent
            out-of-range cells show through, making diagonals flow seamlessly across rows. */}
        <div style={{ backgroundImage: STRIPE }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={machines.map((m) => m.id)} strategy={verticalListSortingStrategy}>
              {isReorderMode ? (
                // All rows rendered — DnD needs real DOM nodes for every item
                machines.map((machine) => (
                  <SortableMachineRow
                    key={machine.id}
                    machine={machine}
                    isSelected={machine.id === selectedId}
                    isBulkSelected={selectedBulkIds.has(machine.id)}
                    {...sharedRowProps}
                  />
                ))
              ) : useVirtualList ? (
                // Virtual rendering: only visible rows are in DOM
                <div className="relative" style={{ height: rowVirtualizer.getTotalSize() }}>
                  {virtualItems.map((virtualRow) => {
                    const machine = machines[virtualRow.index];
                    return (
                      <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        className="absolute top-0 w-full"
                        // Deliberately not using rowVirtualizer.measureElement: rows have a
                        // fixed height (h-16, no wrapping content), so dynamic DOM measurement
                        // is unnecessary and its sub-pixel rounding (from zoom/DPI) drifts
                        // virtualRow.start off exact ROW_HEIGHT multiples — breaking the
                        // stripe background's phase alignment with the parent gradient.
                        style={{ transform: `translateY(${virtualRow.start}px)` }}
                      >
                        <SortableMachineRow
                          machine={machine}
                          isSelected={machine.id === selectedId}
                          isBulkSelected={selectedBulkIds.has(machine.id)}
                          {...sharedRowProps}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Fallback: render all rows (jsdom tests, first render before viewport is measured)
                machines.map((machine) => (
                  <SortableMachineRow
                    key={machine.id}
                    machine={machine}
                    isSelected={machine.id === selectedId}
                    isBulkSelected={selectedBulkIds.has(machine.id)}
                    {...sharedRowProps}
                  />
                ))
              )}
            </SortableContext>
          </DndContext>
        </div>

        {/* Today marker — 2px line at the current time within today's column */}
        {todayIndex !== -1 && !isReorderMode && machines.length > 0 && (
          <div
            className="absolute inset-y-0 w-0.5 bg-primary opacity-80 pointer-events-none z-[5]"
            style={{ left: labelWidth + todayIndex * DAY_WIDTH + nowFraction * DAY_WIDTH - 1 }}
          />
        )}
      </div>
    </div>
  );
}
